import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { FormEvent } from 'react';

import { LIBRARY_LAYOUT_SIZES } from '@mightyhive/theme';

import type { ClientMember, ManagedClient } from '../clientsUtils';
import { formatClientDate } from '../clientsUtils';
import { getClientsPageTokens } from '../getClientsPageTokens';
import { AddMemberForm } from './AddMemberForm';
import { ClientsSearchField } from './ClientsSearchField';
import { MemberTable } from './MemberTable';

export interface MemberPanelProps {
  filteredMembersCount: number;
  loadingMembers: boolean;
  memberEmail: string;
  memberPage: number;
  memberRowsPerPage: number;
  members: ClientMember[];
  memberSearch: string;
  onAddMember: (event: FormEvent) => void;
  onMemberEmailChange: (value: string) => void;
  onMemberPageChange: (page: number) => void;
  onMemberRowsPerPageChange: (rowsPerPage: number) => void;
  onMemberSearchChange: (value: string) => void;
  onRemoveMember: (member: ClientMember) => void;
  pagedMembers: ClientMember[];
  saving: boolean;
  selectedClient: ManagedClient | null;
}

export function MemberPanel({
  filteredMembersCount,
  loadingMembers,
  memberEmail,
  memberPage,
  memberRowsPerPage,
  members,
  memberSearch,
  onAddMember,
  onMemberEmailChange,
  onMemberPageChange,
  onMemberRowsPerPageChange,
  onMemberSearchChange,
  onRemoveMember,
  pagedMembers,
  saving,
  selectedClient,
}: MemberPanelProps): JSX.Element {
  const theme = useTheme();
  const tokens = getClientsPageTokens(theme);

  return (
    <Box
      sx={{
        minWidth: 0,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          border: `1px solid ${tokens.sectionSurfaceBorder}`,
          borderRadius: `${LIBRARY_LAYOUT_SIZES.sectionRadius}px`,
          bgcolor: tokens.sectionSurfaceBg,
          px: { xs: 2, sm: 3 },
          py: { xs: 2.5, sm: 3 },
          overflow: 'hidden',
        }}
      >
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              flexShrink: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h2"
                noWrap
                sx={{
                  fontSize: '20px',
                  fontWeight: 600,
                  lineHeight: '28px',
                  color: tokens.pageHeadingColor,
                }}
              >
                {selectedClient?.name ?? 'Select a client'}
              </Typography>
              {selectedClient ? (
                <Typography variant="body2" sx={{ color: tokens.bodySecondaryColor, mt: 0.5 }}>
                  Created {formatClientDate(selectedClient.createdAt)}
                </Typography>
              ) : null}
            </Box>
            {loadingMembers ? <CircularProgress size={20} /> : null}
          </Box>

          {selectedClient ? (
            <AddMemberForm
              memberEmail={memberEmail}
              disabled={saving}
              onMemberEmailChange={onMemberEmailChange}
              onSubmit={onAddMember}
            />
          ) : null}

          {selectedClient ? (
            <ClientsSearchField
              placeholder="Search members by email or name"
              value={memberSearch}
              onChange={(value) => {
                onMemberSearchChange(value);
                onMemberPageChange(0);
              }}
            />
          ) : null}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              mx: { xs: -2, sm: -3 },
            }}
          >
            <MemberTable
              filteredMembersCount={filteredMembersCount}
              loadingMembers={loadingMembers}
              memberPage={memberPage}
              memberRowsPerPage={memberRowsPerPage}
              membersCount={members.length}
              onMemberPageChange={onMemberPageChange}
              onMemberRowsPerPageChange={onMemberRowsPerPageChange}
              onRemoveMember={onRemoveMember}
              pagedMembers={pagedMembers}
              saving={saving}
              selectedClientName={selectedClient?.name ?? null}
            />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
